import { useCallback, useEffect, useState } from 'react'
import Sheet from './Sheet.jsx'
import { TAG_COLORS, createTag, getTags, getTagsFor, tag, untag } from '../lib/tags.js'

/**
 * Tags on one thing. The same tag is reused everywhere it appears, so a word
 * never quietly comes to mean two things in two places.
 *
 * Typing a name that already exists picks that tag rather than making a
 * near-duplicate — the usual way tag lists rot.
 */
export default function Tags({ target, toast }) {
  const [all, setAll] = useState([])
  const [mine, setMine] = useState(null)
  const [picking, setPicking] = useState(false)

  const load = useCallback(async () => {
    if (!target?.id) { setMine([]); return }
    try {
      const [tags, rows] = await Promise.all([getTags(), getTagsFor(target.type, target.id)])
      setAll(tags)
      setMine(rows)
    } catch {
      setMine([])
    }
  }, [target?.type, target?.id])

  useEffect(() => { load() }, [load])

  if (!target?.id || !mine) return null

  const byId = Object.fromEntries(all.map((t) => [t.id, t]))

  return (
    <div className="tags">
      <div className="tagrow">
        {mine.map((t) => {
          const def = byId[t.tag_id]
          if (!def) return null
          return (
            <span className="tagchip" key={t.id} style={{ borderColor: def.color, color: def.color }}>
              {def.name}
              <button
                type="button"
                aria-label={'Remove ' + def.name}
                onClick={async () => {
                  try {
                    await untag(t.id)
                    load()
                  } catch (e) {
                    toast?.(e.message || 'Could not remove that tag.')
                  }
                }}
              >
                ✕
              </button>
            </span>
          )
        })}
        <button type="button" className="tagchip add" onClick={() => setPicking(true)}>
          + Tag
        </button>
      </div>

      {picking ? (
        <TagSheet
          all={all}
          mine={mine}
          target={target}
          onClose={() => setPicking(false)}
          onDone={() => { setPicking(false); load() }}
          toast={toast}
        />
      ) : null}
    </div>
  )
}

function TagSheet({ all, mine, target, onClose, onDone, toast }) {
  const [q, setQ] = useState('')
  const [color, setColor] = useState(TAG_COLORS[0])
  const [busy, setBusy] = useState(false)

  const already = new Set(mine.map((t) => t.tag_id))
  const typed = q.trim()
  const shown = all.filter((t) => !typed || t.name.toLowerCase().includes(typed.toLowerCase()))
  const exact = all.some((t) => t.name.toLowerCase() === typed.toLowerCase())

  async function apply(t) {
    setBusy(true)
    try {
      await tag(t.id, target.type, target.id)
      onDone()
    } catch (e) {
      toast?.(e.message || 'Could not add that tag.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet title="Tag it" onClose={onClose}>
      <div className="field">
        <input
          type="search"
          placeholder="Find or make a tag"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
        />
      </div>

      {typed && !exact ? (
        <>
          <p className="lbl" style={{ margin: '0 0 8px' }}>New tag colour</p>
          <div className="swatches">
            {TAG_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={'Colour ' + c}
                aria-pressed={c === color}
                className={c === color ? 'on' : ''}
                style={{ background: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
          <button
            type="button"
            className="btn"
            disabled={busy}
            style={{ marginBottom: 16 }}
            onClick={async () => {
              setBusy(true)
              try {
                const made = await createTag(typed, color)
                await tag(made.id, target.type, target.id)
                onDone()
              } catch (e) {
                toast?.(e.message || 'Could not make that tag.')
              } finally {
                setBusy(false)
              }
            }}
          >
            Make “{typed}”
          </button>
        </>
      ) : null}

      {shown.length ? (
        <div className="catpick">
          {shown.map((t) => {
            const on = already.has(t.id)
            return (
              <button
                key={t.id}
                type="button"
                disabled={busy || on}
                style={on ? { opacity: 0.45 } : undefined}
                onClick={() => apply(t)}
              >
                <i style={{ background: t.color, width: 8, height: 8, borderRadius: 2, flex: 'none' }} />
                {t.name}
                {on ? <em className="lbl" style={{ marginLeft: 'auto', fontStyle: 'normal' }}>on</em> : null}
              </button>
            )
          })}
        </div>
      ) : (
        <p className="lbl">{typed ? 'No tag by that name yet.' : 'No tags yet. Type one to make it.'}</p>
      )}
    </Sheet>
  )
}
